import React, {useEffect, useState, useContext} from "react";
import * as Sentry from '@sentry/browser';
import {withAuth} from "../../hoc/Auth";
import {Column, Container, Wrapper} from "../../hoc/Layout";
import Api from "../../Api";
import MerchantTerminalSelect from "../../Components/MerchantTerminalSelect";
import DateRange from "../../Components/Utils/DateRange";
import MessageContext from "../../MessageContext";
import Loader from "../../Components/Utils/Loader";
import Pagination from "react-js-pagination";

import classes from "./styles.module.css";

var TYPE_CREDIT = 1;
var TYPE_COMMISSION = 2;

var TYPE_OPTIONS = [
    {value: "", label: "Все"},
    {value: "1", label: "Зачисление"},
    {value: "2", label: "Комиссия"},
    {value: "3", label: "Выплата мерчанту"},
    {value: "4", label: "Отмена выплаты"},
    {value: "5", label: "Ручная корректировка"},
    {value: "6", label: "Возврат"},
    {value: "7", label: "Чарджбэк"},
    {value: "8", label: "Пополнение"}
];

// Группировка: скрываем строки комиссий, встраиваем их данные в строки зачислений
function groupEntries(entries) {
    // Собираем комиссии по transaction_id
    var commissionByTxId = {};
    var result = [];
    var i;

    for (i = 0; i < entries.length; i++) {
        var e = entries[i];
        if (e.type === TYPE_COMMISSION && e.transaction_id) {
            commissionByTxId[e.transaction_id] = e;
        }
    }

    for (i = 0; i < entries.length; i++) {
        var entry = entries[i];
        // Пропускаем строки комиссий — они уйдут в аккордеон
        if (entry.type === TYPE_COMMISSION) continue;

        var commissionEntry = null;
        if (entry.type === TYPE_CREDIT && entry.transaction_id) {
            commissionEntry = commissionByTxId[entry.transaction_id] || null;
        }

        result.push({
            entry: entry,
            commissionEntry: commissionEntry
        });
    }

    return result;
}

// Пересчёт summary: приход = нетто, расход без комиссий
function adjustSummary(summary, entries) {
    if (!summary) return null;
    var totalCommission = 0;
    for (var i = 0; i < entries.length; i++) {
        if (entries[i].type === TYPE_COMMISSION) {
            totalCommission += Math.abs(entries[i].amount || 0);
        }
    }
    return {
        total_credit: (summary.total_credit || 0) - totalCommission,
        total_debit: (summary.total_debit || 0) - totalCommission,
        total_commission: summary.total_commission || 0
    };
}

var LIMIT = 50;

var Statement = withAuth(function(props) {
    var token = props.token;
    var onMessage = useContext(MessageContext).onMessage;

    var stateEntries = useState([]);
    var entries = stateEntries[0];
    var setEntries = stateEntries[1];

    var stateLoading = useState(true);
    var loading = stateLoading[0];
    var setLoading = stateLoading[1];

    var stateCount = useState(0);
    var count = stateCount[0];
    var setCount = stateCount[1];

    var statePage = useState(1);
    var page = statePage[0];
    var setPage = statePage[1];

    var stateSummary = useState(null);
    var summary = stateSummary[0];
    var setSummary = stateSummary[1];

    var stateSelectedMs = useState("");
    var selectedMs = stateSelectedMs[0];
    var setSelectedMs = stateSelectedMs[1];

    var stateFilterDate = useState({});
    var filterDate = stateFilterDate[0];
    var setFilterDate = stateFilterDate[1];

    var stateSelectedType = useState("");
    var selectedType = stateSelectedType[0];
    var setSelectedType = stateSelectedType[1];

    var stateExporting = useState(false);
    var exporting = stateExporting[0];
    var setExporting = stateExporting[1];

    var stateExpanded = useState({});
    var expanded = stateExpanded[0];
    var setExpanded = stateExpanded[1];

    function toggleExpanded(id) {
        var next = {};
        for (var k in expanded) { next[k] = expanded[k]; }
        next[id] = !next[id];
        setExpanded(next);
    }

    function handleMsSelect(msId) {
        setSelectedMs(msId);
        setPage(1);
    }

    var dateFrom = filterDate.startDate || "";
    var dateTo = filterDate.endDate || "";

    // Load statement when filters change
    useEffect(function() {
        if (!selectedMs || !dateFrom || !dateTo) return;
        setLoading(true);
        var api = new Api({token: token});
        var params = {
            merchant_settings_id: selectedMs,
            date_from: dateFrom,
            date_to: dateTo,
            page: page,
            limit: LIMIT
        };
        if (selectedType) {
            params.type = selectedType;
        }
        api.getMerchantStatement(params).then(function(r) {
            setEntries(r.entries || []);
            setCount(r.count || 0);
            setSummary(r.summary || null);
            setLoading(false);
        }).catch(function(err) {
            Sentry.captureException(err);
            setLoading(false);
            onMessage({type: "error", message: "Ошибка загрузки выписки"});
        });
    }, [token, selectedMs, dateFrom, dateTo, selectedType, page]);

    function handleTypeChange(e) {
        setSelectedType(e.target.value);
        setPage(1);
    }

    function handlePageChange(p) {
        setPage(p);
    }

    function handleExportExcel() {
        if (!selectedMs || !dateFrom || !dateTo) return;
        setExporting(true);
        var params = {
            merchant_settings_id: selectedMs,
            date_from: dateFrom,
            date_to: dateTo
        };
        if (selectedType) params.type = selectedType;
        new Api({token: token}).exportMerchantStatement(params).then(function() {
            setExporting(false);
        }).catch(function(err) {
            Sentry.captureException(err);
            setExporting(false);
            onMessage({type: "error", message: "Ошибка экспорта"});
        });
    }

    function handleSettlementExcel(settlementId) {
        new Api({token: token}).downloadSettlementExcel(settlementId).catch(function(err) {
            Sentry.captureException(err);
            onMessage({type: "error", message: "Ошибка скачивания Excel"});
        });
    }

    return (
        <Wrapper>
            <Container>
                <Column width="full">
                    <div className={classes.Card}>
                        <div className={classes.Header}>
                            <h5 className={classes.Title}>Выписка</h5>
                        </div>
                        <div className={classes.Content}>
                        {/* Selector */}
                        <div className={classes.SelectorBlock}>
                            <MerchantTerminalSelect
                                mode="user"
                                token={token}
                                stackOnMobile={true}
                                onSelect={handleMsSelect}
                                onDataLoaded={function() { setLoading(false); }}
                            />
                        </div>

                        {/* Filters */}
                        <div className={classes.FiltersRow}>
                                <div className={classes.DateGroup}>
                                    <DateRange onSelectDate={function(range) { setFilterDate(range); setPage(1); }} defaultPeriod="week"/>
                                </div>
                                <div className={classes.FilterGroup}>
                                    <label>Тип операции</label>
                                    <select className={classes.TypeSelect} value={selectedType} onChange={handleTypeChange}>
                                        {TYPE_OPTIONS.map(function(opt) {
                                            return (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            );
                                        })}
                                    </select>
                                </div>
                                <div className={classes.FilterGroup}>
                                    <button
                                        className={classes.BtnOutline}
                                        onClick={handleExportExcel}
                                        disabled={exporting || !selectedMs || !dateFrom || !dateTo}
                                    >
                                        {exporting ? "Экспорт..." : "Экспорт Excel"}
                                    </button>
                                </div>
                        </div>

                        {/* Summary cards */}
                        {summary && (function() {
                            var adj = adjustSummary(summary, entries);
                            return (
                                <div className={classes.SummaryRow}>
                                    <div className={classes.SummaryCard}>
                                        <small>Приход</small>
                                        <div style={{color: "#4caf50", fontWeight: 600, fontSize: "1.1rem"}}>+{((adj.total_credit || 0) / 100).toFixed(2)}</div>
                                    </div>
                                    <div className={classes.SummaryCard}>
                                        <small>Расход</small>
                                        <div style={{color: "#f44336", fontWeight: 600, fontSize: "1.1rem"}}>-{((adj.total_debit || 0) / 100).toFixed(2)}</div>
                                    </div>
                                    {adj.total_commission > 0 && (
                                        <div className={classes.SummaryCard}>
                                            <small>Комиссия</small>
                                            <div style={{color: "#ff9800", fontWeight: 600, fontSize: "1.1rem"}}>{(adj.total_commission / 100).toFixed(2)}</div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()}

                        {/* Table */}
                        {loading ? <Loader/> : (
                            <div>
                                <div className={classes.TableScroll}>
                                <table className={classes.Table}>
                                    <thead>
                                        <tr>
                                            <th>Дата</th>
                                            <th>Операция</th>
                                            <th>ID транзакции</th>
                                            <th>Карта</th>
                                            <th>Settlement</th>
                                            <th>Приход</th>
                                            <th>Расход</th>
                                            <th>Комиссия</th>
                                            <th>USDT</th>
                                            <th>Баланс</th>
                                            <th>Описание</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.length === 0 && (
                                            <tr>
                                                <td colSpan="12" className="text-center text-muted">
                                                    {(!dateFrom || !dateTo) ? "Выберите период для просмотра выписки" : "Нет записей"}
                                                </td>
                                            </tr>
                                        )}
                                        {[].concat.apply([], groupEntries(entries).map(function(grouped) {
                                            var entry = grouped.entry;
                                            var commEntry = grouped.commissionEntry;
                                            var hasAccordion = !!commEntry;
                                            var isOpen = hasAccordion && expanded[entry.id];

                                            // Нетто: приход минус комиссия
                                            var isPositive = parseInt(entry.amount, 10) > 0;
                                            var displayAmountFloat = entry.amount_float;
                                            // Баланс: берём из строки комиссии (после вычета), если есть
                                            var displayBalanceFloat = hasAccordion ? commEntry.balance_after_float : entry.balance_after_float;
                                            if (hasAccordion && isPositive && commEntry.amount_float) {
                                                displayAmountFloat = entry.amount_float - Math.abs(commEntry.amount_float);
                                            }

                                            var rows = [];

                                            // Основная строка
                                            rows.push(
                                                React.createElement("tr", {
                                                    key: entry.id,
                                                    className: hasAccordion ? classes.AccordionRow : "",
                                                    onClick: hasAccordion ? function() { toggleExpanded(entry.id); } : undefined,
                                                    style: hasAccordion ? {cursor: "pointer"} : undefined
                                                },
                                                    React.createElement("td", {style: {whiteSpace: "nowrap"}},
                                                        hasAccordion ? React.createElement("span", {className: classes.Arrow + (isOpen ? " " + classes.ArrowOpen : "")}, "\u25B6") : null,
                                                        " ",
                                                        new Date(entry.created_at).toLocaleString('ru-RU')
                                                    ),
                                                    React.createElement("td", null, entry.type_name),
                                                    React.createElement("td", null,
                                                        entry.transaction_id ? React.createElement("a", {
                                                            href: "/transactions/" + JSON.stringify({filters: [{id: entry.transaction_id, locale: "Search.label.operationId"}]}),
                                                            style: {color: "#0d6efd"},
                                                            onClick: function(e) { e.stopPropagation(); }
                                                        }, entry.transaction_id) : ''
                                                    ),
                                                    React.createElement("td", {style: {whiteSpace: "nowrap"}}, entry.card_mask || ''),
                                                    React.createElement("td", null, entry.settlement_id || ''),
                                                    React.createElement("td", {className: "text-success"}, isPositive ? displayAmountFloat.toFixed(2) : ''),
                                                    React.createElement("td", {className: "text-danger"}, !isPositive ? Math.abs(displayAmountFloat).toFixed(2) : ''),
                                                    React.createElement("td", null, entry.commission_float != null ? entry.commission_float.toFixed(2) : ''),
                                                    React.createElement("td", null, entry.amount_usdt != null ? Number(entry.amount_usdt).toFixed(4) : ''),
                                                    React.createElement("td", null, displayBalanceFloat.toFixed(2)),
                                                    React.createElement("td", null, entry.description || ''),
                                                    React.createElement("td", null,
                                                        entry.type === 3 && entry.settlement_id ? React.createElement("button", {
                                                            className: "btn btn-outline-primary btn-sm",
                                                            style: {fontSize: ".75rem", padding: "2px 6px"},
                                                            onClick: function(e) { e.stopPropagation(); handleSettlementExcel(entry.settlement_id); },
                                                            title: "Скачать Excel детализации"
                                                        }, "Excel") : null
                                                    )
                                                )
                                            );

                                            // Раскрытая детализация
                                            if (isOpen) {
                                                var grossFloat = entry.amount_float;
                                                var feeFloat = Math.abs(commEntry.amount_float);
                                                var netFloat = grossFloat - feeFloat;

                                                rows.push(
                                                    React.createElement("tr", {key: entry.id + "-detail", className: classes.DetailRow},
                                                        React.createElement("td", {colSpan: "12"},
                                                            React.createElement("div", {className: classes.DetailContent},
                                                                React.createElement("span", null, "Сумма операции: ", React.createElement("b", null, grossFloat.toFixed(2))),
                                                                React.createElement("span", {className: "text-danger"}, " \u2212 Комиссия: ", React.createElement("b", null, feeFloat.toFixed(2))),
                                                                React.createElement("span", {className: "text-success"}, " = Зачислено: ", React.createElement("b", null, netFloat.toFixed(2)))
                                                            )
                                                        )
                                                    )
                                                );
                                            }

                                            return rows;
                                        }))}
                                    </tbody>
                                </table>
                                </div>

                                {/* Pagination */}
                                {count > LIMIT && (
                                    <div className={classes.PaginationWrap}>
                                        <Pagination
                                            activePage={page}
                                            itemsCountPerPage={LIMIT}
                                            totalItemsCount={count}
                                            pageRangeDisplayed={5}
                                            onChange={handlePageChange}
                                            itemClass="page-item"
                                            linkClass="page-link"
                                            activeLinkClass={classes.ActiveLink}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        </div>
                    </div>
                </Column>
            </Container>
        </Wrapper>
    );
});

export default Statement;



// WEBPACK FOOTER //
// ./src/PagesOfUser/Statement/index.js